/**
 * Global axios setup:
 * - `withCredentials: true` so httpOnly cookies are sent on every request
 * - 401 interceptor: tries /api/auth/refresh once, then retries the original call
 *
 * Tokens are NEVER stored in localStorage. The browser handles cookies for us.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// --- CSRF double-submit: echo the readable csrf_token cookie back as a header
// on state-changing requests so the backend can verify it. ---
const CSRF_COOKIE = 'csrf_token';
const MUTATING = ['post', 'put', 'patch', 'delete'];

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)'));
  return match ? decodeURIComponent(match.pop()) : null;
};

axios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (MUTATING.includes(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = token;
    }
  }
  return config;
});

let refreshPromise = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!original || !error.response) return Promise.reject(error);

    const status = error.response.status;
    const isRefreshCall = original.url && original.url.includes('/auth/refresh');
    const isLoginCall = original.url && (
      original.url.includes('/admin/login') ||
      original.url.includes('/pro/login')
    );

    if (status === 401 && !original._retry && !isRefreshCall && !isLoginCall) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return axios(original);
      } catch (refreshErr) {
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
